import { Box, Grid, Heading, LinkBox, LinkOverlay, Stack, StackProps, Text, VStack } from "@biblioteksentralen/react";
import { ReactNode } from "react";

import { TruncatedList } from "../../searchAndCollection/components/TruncatedList";
import { ItemCoverImage } from "../../searchAndCollection/components/ItemCoverImage";
import { getPath } from "@libry-content/common";
import { useTranslation } from "../../utils/hooks/useTranslation";
import InternalLinkOverlay from "../InternalLinkOverlay";
import { useReadalikes } from "./useForrigeBokData";
import { CordataWork } from "@libry-content/integrations";

type Props = StackProps & {
  workId: string;
};

const ReadAlikes = ({ workId, ...chakraProps }: Props) => {
  const { t } = useTranslation();
  const { data } = useReadalikes({ workId, limit: 6 });

  if (!data?.length) return null;

  return (
    <Stack {...chakraProps} spacing={4}>
      <VStack spacing={1} paddingBottom="1rem" alignItems="flex-start">
        <Heading size="md">{t("Lignende bøker")}</Heading>
        <Text>{t("Andre bøker fra samlingen vår med lignende leseopplevelse. Utvalget er gjort automatisk.")}</Text>
      </VStack>
      <Grid gridTemplateColumns="repeat(auto-fill, minmax(7rem, 1fr))" gridGap="2rem" alignItems="end">
        {data?.map((work) => (
          <Readalike readalike={new CordataWork(work)} key={work.id} />
        ))}
      </Grid>
    </Stack>
  );
};

const Readalike = ({ readalike }: { readalike: CordataWork }) => {
  const { lang } = useTranslation();
  const { formattedTitle, titleLanguagesSignature } = readalike.getRepresentativeTitleData();
  const mainContributor = readalike.mainContributor;
  return (
    <CommonReadalikeLayout
      lang={titleLanguagesSignature !== lang ? titleLanguagesSignature : undefined}
      image={<ItemCoverImage item={readalike} showFavoriteButton />}
      title={formattedTitle}
      path={getPath({
        id: readalike.id,
        type: "Work",
        title: { mainTitle: formattedTitle ?? "N/A" },
      })}
      author={
        mainContributor && (
          <TruncatedList
            items={[mainContributor.agent]}
            max={2}
            linkProps={{ textDecoration: "none", _hover: { textDecoration: "underline" } }}
          />
        )
      }
      isInternalLink
    />
  );
};

const CommonReadalikeLayout = (props: {
  image: ReactNode;
  path?: string;
  title?: string;
  author?: ReactNode;
  isInternalLink?: boolean;
  lang?: string | undefined;
}) => (
  <LinkBox>
    <Stack role="group">
      {props.image}
      <Box minHeight="4.3rem">
        <LinkOverlay
          as={props.isInternalLink ? InternalLinkOverlay : undefined}
          href={props.path}
          _groupHover={{ textDecoration: "underline" }}
          noOfLines={2}
        >
          <Heading as="h3" size="xs" lang={props.lang}>
            {props.title}
          </Heading>
        </LinkOverlay>
        <Box fontSize="xs">{props.author}</Box>
      </Box>
    </Stack>
  </LinkBox>
);

export default ReadAlikes;
